import React from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, X } from 'lucide-react'
import { Menu as HeadlessMenu, Transition } from '@headlessui/react'
import { Fragment } from 'react'

export type FilterType =  'branch' |  'order_type' | 'payment_method' |'platforms' 

interface FilterOption {
  value: string
  label: string
}

interface DropdownFiltersProps {
  filters: Array<{
    type: FilterType
    label: string
    options: FilterOption[]
    selectedValue?: string
    onSelect: (value: string) => void
  }>
  isRTL?: boolean
  onClearAll?: () => void
}

const DropdownFilters: React.FC<DropdownFiltersProps> = ({
  filters,
  isRTL = false,
  onClearAll,
}) => {
  const { t } = useTranslation()

  // Count how many filters are selected
  const selectedFiltersCount = filters.filter((filter) => {
    const selectedOption = filter.selectedValue
      ? filter.options.find((opt) => opt.value === filter.selectedValue)
      : null
    
    return filter.selectedValue && 
      filter.selectedValue.toLowerCase() !== 'all' && 
      selectedOption && 
      selectedOption.value.toLowerCase() !== 'all'
  }).length

  const handleClearAll = () => {
    if (onClearAll) {
      onClearAll()
    } else {
      // Fallback: clear all filters by calling onSelect('all') for each
      filters.forEach((filter) => {
        filter.onSelect('all')
      })
    }
  }

  return (
    <div className={`flex-1 flex items-center gap-3 `}>
      {filters.map((filter) => {
        const selectedOption = filter.selectedValue
          ? filter.options.find((opt) => opt.value === filter.selectedValue)
          : null
        
        // Check if a filter is selected (not 'all' and not undefined)
        const isFilterSelected = filter.selectedValue && 
          filter.selectedValue.toLowerCase() !== 'all' && 
          selectedOption && 
          selectedOption.value.toLowerCase() !== 'all'
        
        // Get the selected option label, excluding 'all' option
        const selectedLabel = isFilterSelected && selectedOption 
          ? selectedOption.label 
          : null

        const handleIconClick = (e: React.MouseEvent) => {
          e.stopPropagation()
          e.preventDefault()
          if (isFilterSelected) {
            // Clear the filter by selecting 'all'
            filter.onSelect('all')
          }
        }

        return (
          <HeadlessMenu key={filter.type} as="div" className="relative">
            <HeadlessMenu.Button
              className="h-10 pl-2.5 pr-3 py-1 bg-white border border-gray-200 rounded-xl inline-flex justify-end items-center gap-2">
              {isFilterSelected ? (
                <>
                  {selectedLabel && (
                    <span className="text-right justify-start text-text-primary text-sm font-medium font-['IBM_Plex_Sans_Arabic'] leading-5">
                      {selectedLabel}
                    </span>
                  )}
                  <X 
                    className="w-5 h-5 text-zinc-300 cursor-pointer hover:text-zinc-500" 
                    onClick={handleIconClick}
                  />
                
                </>
              ) : (
                <>
                  <span className="text-right justify-start text-text-primary text-sm font-medium font-['IBM_Plex_Sans_Arabic'] leading-5">
                    {filter.label}
                  </span>
                  <ChevronDown className="w-5 h-5 text-zinc-300" />

                </>
              )}
            </HeadlessMenu.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <HeadlessMenu.Items
              className={`
                absolute top-full mt-2 w-48 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-50
              `}
            >
              <div className="py-1">
                {filter.options
                  .filter(option => option.value.toLowerCase() !== 'all') // Remove 'all' option from dropdown
                  .map((option) => (
                  <HeadlessMenu.Item key={option.value}>
                    {({ active }) => (
                      <button
                        onClick={() => filter.onSelect(option.value)}
                        className={`
                            w-full px-4 py-2 text-sm text-left transition-colors
                          ${active ? 'bg-gray-100' : ''}
                          ${filter.selectedValue === option.value ? 'bg-gray-200 bg-opacity-80 text-[var(--brand-)]' : 'text-gray-700'}
                        `}
                      >
                        {option.label}
                      </button>
                    )}
                  </HeadlessMenu.Item>
                ))}
              </div>
            </HeadlessMenu.Items>
          </Transition>
        </HeadlessMenu>
        )
      })}
      
      {/* Clear All button - appears when more than one filter is selected */}
      {selectedFiltersCount > 1 && (
        <button
          onClick={handleClearAll}
          className="h-10 px-3 py-1 bg-white border border-gray-200 rounded-xl flex items-center gap-2 flex-row-reverse hover:bg-gray-50 transition-colors"
        >
          <X className="w-5 h-5 text-zinc-300" />
          <span className="text-right justify-start text-text-primary text-sm font-medium font-['IBM_Plex_Sans_Arabic'] leading-5">
            {t('orders.filter.clear_all') || 'مسح الكل'}
          </span>
        </button>
      )}
    </div>
  )
}

export default DropdownFilters

